import {
  applyPhoneAndEmail,
  type GeoResponse,
  type PhoneConfig,
} from './utils/contactSubstitution'
import { PHONE_BY_LOCATION } from './config/phoneByLocation'
import { LOCATION_ALIASES } from './config/locationAliases'

declare global {
  interface Window {
    ym?: (...args: any[]) => void
    engvarDynamicContacts?: Record<string, any>
    __dynamicContactsInitialized?: boolean
    __dynamicContactsData?: {
      visitorId: string
      email: string
      geo: GeoResponse
      phone: PhoneConfig
    }
  }
}

const METRIKA_ID = 88178339

const DEFAULT_PHONE_DISPLAY = '+7 (812) 408-1913'
const DEFAULT_PHONE_HREF = '+78124081913'

function waitForYm(timeout = 10000): Promise<(...args: any[]) => void> {
  return new Promise((resolve, reject) => {
    const startedAt = Date.now()

    const check = () => {
      if (typeof window !== 'undefined' && typeof window.ym === 'function') {
        resolve(window.ym)
        return
      }

      if (Date.now() - startedAt > timeout) {
        reject(new Error('Yandex Metrica ym not found'))
        return
      }

      setTimeout(check, 200)
    }

    check()
  })
}

function generateVisitorId(): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'
  let result = ''

  for (let i = 0; i < 5; i += 1) {
    result += chars.charAt(Math.floor(Math.random() * chars.length))
  }

  return result
}

function getOrCreateVisitorId(): string {
  const storageKey = 'engvar_visitor_id'
  const existing = localStorage.getItem(storageKey)

  if (existing) return existing

  const newId = generateVisitorId()
  localStorage.setItem(storageKey, newId)
  return newId
}

function buildEmail(visitorId: string): string {
  return `sales+${visitorId}@engvar.ru`
}

function normalizeLocationName(value?: string | null): string {
  if (!value) return ''

  const cleaned = value
    .replace(/^г\.\s*/i, '')
    .replace(/^г\s+/i, '')
    .replace(/^город\s+/i, '')
    .trim()

  return LOCATION_ALIASES[cleaned] || cleaned
}

function getPhoneByGeo(geo: GeoResponse): PhoneConfig {
  const candidates = [
    geo.city,
    geo.settlement,
    geo.area,
    geo.region,
  ]
    .map(normalizeLocationName)
    .filter(Boolean) as string[]

  for (const name of candidates) {
    if (PHONE_BY_LOCATION[name]) {
      return PHONE_BY_LOCATION[name]
    }
  }

  return {
    display: DEFAULT_PHONE_DISPLAY,
    href: DEFAULT_PHONE_HREF,
  }
}

async function getGeo(): Promise<GeoResponse> {
  try {
    const response = await fetch('/api/geo', {
      method: 'GET',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
      },
    })

    const contentType = response.headers.get('content-type') || ''

    if (!response.ok) {
      console.warn(`Geo request failed: ${response.status}`)
      return { city: null, settlement: null, area: null, region: null, ip: null }
    }

    if (!contentType.includes('application/json')) {
      console.warn(`Geo endpoint returned non-JSON: ${contentType}`)
      return { city: null, settlement: null, area: null, region: null, ip: null }
    }

    return await response.json()
  } catch (error) {
    console.warn('Geo request error:', error)
    return { city: null, settlement: null, area: null, region: null, ip: null }
  }
}

async function sendToMetrika(payload: {
  visitorId: string
  city?: string | null
  region?: string | null
  phone: string
  email: string
  ip?: string | null
}): Promise<void> {
  try {
    const ym = await waitForYm()

    ym(METRIKA_ID, 'userParams', {
      UserID: payload.visitorId,
      visitor_id: payload.visitorId,
      visitor_city: payload.city || '',
      visitor_region: payload.region || '',
      dynamic_phone: payload.phone,
      dynamic_email: payload.email,
      visitor_ip: payload.ip || '',
    })

    ym(METRIKA_ID, 'reachGoal', 'dynamic_contacts_assigned', {
      visitor_id: payload.visitorId,
      city: payload.city || '',
      region: payload.region || '',
      phone: payload.phone,
      email: payload.email,
    })
  } catch (error) {
    console.warn('Metrica send error:', error)
  }
}

function reapplyDynamicContacts(attempts = 8): void {
  const data = window.__dynamicContactsData
  if (!data) return

  const apply = () => {
    applyPhoneAndEmail(data.phone, data.email, data.geo)
  }

  apply()

  for (let i = 1; i <= attempts; i += 1) {
    setTimeout(apply, i * 150)
  }
}

function setupRouteTracking(): void {
  const originalPushState = history.pushState
  const originalReplaceState = history.replaceState

  history.pushState = function (...args) {
    originalPushState.apply(this, args)
    reapplyDynamicContacts()
  }

  history.replaceState = function (...args) {
    originalReplaceState.apply(this, args)
    reapplyDynamicContacts()
  }

  window.addEventListener('popstate', () => {
    reapplyDynamicContacts()
  })
}

async function initDynamicContacts(): Promise<void> {
  if (window.__dynamicContactsInitialized) return
  window.__dynamicContactsInitialized = true

  try {
    const visitorId = getOrCreateVisitorId()
    const email = buildEmail(visitorId)
    const geo = await getGeo()
    const phone = getPhoneByGeo(geo)

    window.__dynamicContactsData = {
      visitorId,
      email,
      geo,
      phone,
    }

    reapplyDynamicContacts()

    void sendToMetrika({
      visitorId,
      city: geo.city,
      region: geo.region,
      phone: phone.display,
      email,
      ip: geo.ip,
    })

    window.engvarDynamicContacts = {
      visitor_id: visitorId,
      city: geo.city || '',
      settlement: geo.settlement || '',
      area: geo.area || '',
      region: geo.region || '',
      ip: geo.ip || '',
      phone,
      email,
    }

    setupRouteTracking()
  } catch (error) {
    console.error('dynamicContacts init error:', error)
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    void initDynamicContacts()
  })
} else {
  void initDynamicContacts()
}

export {}
